import cronParser from 'cron-parser';
import cronstrue from 'cronstrue';

import {
  convertUTCToLocalTime,
  getBrowserTimezone,
  localWallClockHourToUtcHour,
  utcHourToLocalWallClockHour,
} from './timezoneData';

/**
 * Parse a cron expression and extract its parts
 */
export function parseCronExpression(expression: string): {
  minute: string;
  hour: string;
  dayOfMonth: string;
  month: string;
  dayOfWeek: string;
  second?: string;
} | null {
  if (!expression) return null;

  const parts = expression.trim().split(/\s+/);

  // Handle 5-part or 6-part cron
  if (parts.length === 5) {
    return {
      minute: parts[0],
      hour: parts[1],
      dayOfMonth: parts[2],
      month: parts[3],
      dayOfWeek: parts[4],
    };
  } else if (parts.length === 6) {
    // EventBridge 6-field format: minute hour dayOfMonth month dayOfWeek year
    // parts[5] is the year field — NOT day-of-week (that would be Quartz format)
    return {
      minute: parts[0],
      hour: parts[1],
      dayOfMonth: parts[2],
      month: parts[3],
      dayOfWeek: parts[4],
    };
  }

  return null;
}

/**
 * Generate a cron expression from parts
 */
export function generateCronExpression(parts: {
  minute: string;
  hour: string;
  dayOfMonth: string;
  month: string;
  dayOfWeek: string;
  second?: string;
}): string {
  const { second, minute, hour, dayOfMonth, month, dayOfWeek } = parts;

  if (second !== undefined) {
    return `${second} ${minute} ${hour} ${dayOfMonth} ${month} ${dayOfWeek}`;
  }

  return `${minute} ${hour} ${dayOfMonth} ${month} ${dayOfWeek}`;
}

/**
 * Strip a cron expression to 5 fields for cronstrue compatibility.
 *
 * Our cron expressions use EventBridge format (6 fields: min hour dom month dow year).
 * cronstrue interprets 6 fields as Quartz format (sec min hour dom month dow),
 * treating the first field as SECONDS — which gives wrong results.
 * Stripping to 5 fields (min hour dom month dow) avoids this.
 */
function toFiveFieldCron(cronExpression: string): string {
  const parts = cronExpression.trim().split(/\s+/);
  if (parts.length >= 6) {
    return parts.slice(0, 5).join(' ');
  }
  return cronExpression;
}

/**
 * Sanitize a cron expression so cronstrue can parse it.
 * Handles edge cases produced by UTC↔local conversion:
 *  - `0-0/1` (equal start/end with step) → `0`
 *  - `5-5` (equal start/end range) → `5`
 *  - `?` → `*`  (EventBridge uses `?`, cronstrue prefers `*` in 5-field)
 */
/**
 * Collapse equal-range hour/minute fields like `5-5/1` → `5`.
 * Must match the whole field (or comma segment) — a global regex would
 * falsely match `2-2` inside `2-21/9` and corrupt the expression.
 */
function sanitizeEqualRangeField(field: string): string {
  if (field.includes(',')) {
    return field
      .split(',')
      .map((segment) => sanitizeEqualRangeField(segment.trim()))
      .join(',');
  }

  return field.replace(/^(\d+)-\1(?:\/\d+)?$/, '$1');
}

function sanitizeCronForCronstrue(cronExpression: string): string {
  const cron = toFiveFieldCron(cronExpression);

  const parts = cron.split(/\s+/);
  if (parts.length < 5) return cron;

  for (let i = 0; i < parts.length; i++) {
    let field = parts[i];

    // Replace `?` with `*`
    if (field === '?') {
      field = '*';
    }

    field = sanitizeEqualRangeField(field);

    parts[i] = field;
  }

  return parts.join(' ');
}

const CRONSTRUE_ERROR_PREFIX = 'An error occurred';

/**
 * Get human-readable description of a cron expression using cronstrue
 */
export function formatCronString(cronExpression: string): string {
  if (!cronExpression) return '';

  try {
    const sanitized = sanitizeCronForCronstrue(cronExpression);
    const result = cronstrue.toString(sanitized, {
      throwExceptionOnParseError: false,
      use24HourTimeFormat: false,
    });
    if (result.startsWith(CRONSTRUE_ERROR_PREFIX)) {
      return cronExpression;
    }
    return result;
  } catch {
    return cronExpression;
  }
}

/**
 * Convert a single UTC cron hour to local wall-clock hour (inverse of local→UTC mapping).
 */
function utcHourToLocal(utcHour: number, viewerTimeZone: string): number {
  return utcHourToLocalWallClockHour(utcHour, viewerTimeZone);
}

/**
 * Convert a local wall-clock hour (:00) to UTC cron hour.
 */
function localHourToUtc(localHour: number, viewerTimeZone: string): number {
  return localWallClockHourToUtcHour(localHour, viewerTimeZone);
}

const HOUR_RANGE_STEP_PATTERN = /^(\d+)-(\d+)(?:\/(\d+))?$/;

/**
 * Merge EventBridge midnight-crossing hour fields like `21-23/9,0-16/9` into
 * a single range `21-16/9`. Matches legacy dataProtect splitMultiple behaviour.
 */
export function mergeCrossingMidnightHourField(hourField: string): string {
  if (!hourField.includes(',')) {
    return hourField;
  }

  const segments = hourField.split(',').map((s) => s.trim());
  if (segments.length !== 2) {
    return hourField;
  }

  const parsed = segments.map((seg) => {
    const match = seg.match(HOUR_RANGE_STEP_PATTERN);
    if (!match) return null;
    return {
      start: parseInt(match[1], 10),
      end: parseInt(match[2], 10),
      step: match[3] ? parseInt(match[3], 10) : undefined,
    };
  });

  if (parsed[0] === null || parsed[1] === null) {
    return hourField;
  }

  const [first, second] = parsed;
  if (first.step !== undefined && second.step !== undefined && first.step !== second.step) {
    return hourField;
  }

  // Evening segment before morning segment (e.g. 21-23 then 0-16)
  if (first.start <= second.start) {
    return hourField;
  }

  const step = first.step ?? second.step;
  if (step !== undefined) {
    return `${first.start}-${second.end}/${step}`;
  }
  return `${first.start}-${second.end}`;
}

/**
 * Convert UTC hour range to local hours.
 * Uses Date API so fractional-offset timezones (IST +5:30, etc.) produce
 * integer hours instead of decimals like `16.5`.
 */
export function convertUtcToLocalHours(
  utcRange: string,
  viewerTimeZone: string = getBrowserTimezone()
): string {
  if (!utcRange || utcRange === '*') return utcRange;

  const merged = mergeCrossingMidnightHourField(utcRange);

  if (merged.includes(',') && merged === utcRange) {
    return merged
      .split(',')
      .map((seg) => convertUtcToLocalHours(seg.trim(), viewerTimeZone))
      .join(',');
  }

  // Handle step expressions like "9-17/1"
  const stepMatch = merged.match(HOUR_RANGE_STEP_PATTERN);
  if (stepMatch) {
    const [, startStr, endStr, stepStr] = stepMatch;
    const start = utcHourToLocal(parseInt(startStr, 10), viewerTimeZone);
    const end = utcHourToLocal(parseInt(endStr, 10), viewerTimeZone);
    const step = stepStr ? parseInt(stepStr, 10) : 1;

    if (start === end) {
      return start.toString();
    }

    if (start > end) {
      return step > 1 ? `${start}-23/${step},0-${end}/${step}` : `${start}-23,0-${end}`;
    }

    return step > 1 ? `${start}-${end}/${step}` : `${start}-${end}`;
  }

  // Handle single hour
  const singleMatch = merged.match(/^(\d+)$/);
  if (singleMatch) {
    return utcHourToLocal(parseInt(singleMatch[1], 10), viewerTimeZone).toString();
  }

  return merged;
}

/**
 * Convert local hours to UTC.
 * Uses Date API so fractional-offset timezones produce integer hours.
 */
export function convertLocalToUtcHours(
  localRange: string,
  viewerTimeZone: string = getBrowserTimezone()
): string {
  if (!localRange || localRange === '*') return localRange;

  const stepMatch = localRange.match(/^(\d+)-(\d+)(?:\/(\d+))?$/);
  if (stepMatch) {
    const [, startStr, endStr, stepStr] = stepMatch;
    const start = localHourToUtc(parseInt(startStr, 10), viewerTimeZone);
    const end = localHourToUtc(parseInt(endStr, 10), viewerTimeZone);
    const step = stepStr ? parseInt(stepStr, 10) : 1;

    if (start > end) {
      return step > 1 ? `${start}-23/${step},0-${end}/${step}` : `${start}-23,0-${end}`;
    }

    return step > 1 ? `${start}-${end}/${step}` : `${start}-${end}`;
  }

  const singleMatch = localRange.match(/^(\d+)$/);
  if (singleMatch) {
    return localHourToUtc(parseInt(singleMatch[1], 10), viewerTimeZone).toString();
  }

  return localRange;
}

/** Comma-separated list of integer hours only (e.g. `5,13,21`). */
function isCommaSeparatedSimpleHours(hourField: string): boolean {
  return hourField.split(',').every((segment) => /^\d+$/.test(segment.trim()));
}

/**
 * Convert a full cron expression from UTC to local time.
 *
 * For simple crons (specific minute + specific hour, no ranges/steps),
 * converts BOTH minute and hour using the Date API — critical for
 * fractional-offset timezones (e.g. IST +5:30 shifts 14:00 UTC → 19:30 local,
 * so both hour AND minute fields must change).
 *
 * Comma-separated hour lists with a fixed minute use the same full conversion
 * per UTC fire time (e.g. `35 5,13,21` → `5 3,11,19` in IST).
 *
 * For complex hour patterns (ranges, steps, wildcards), only the hour field is
 * converted (minute-level precision is lost, matching legacy behavior).
 */
export function convertCronUtcToLocal(
  cronExpression: string,
  viewerTimeZone: string = getBrowserTimezone()
): string {
  const parts = cronExpression.trim().split(/\s+/);
  if (parts.length < 5) return cronExpression;

  const minuteField = parts[0];
  const hourField = parts[1];

  if (hourField === '*' || hourField === '?') {
    return parts.slice(0, 5).join(' ');
  }

  const isSimpleMinute = /^\d+$/.test(minuteField);
  const isSimpleHour = /^\d+$/.test(hourField);

  const convertSimpleUtcFire = (utcHour: number, utcMinute: number) =>
    convertUTCToLocalTime(utcHour, utcMinute, viewerTimeZone);

  if (isSimpleMinute && isCommaSeparatedSimpleHours(hourField)) {
    const utcMinute = parseInt(minuteField, 10);
    const localHours: number[] = [];
    let localMinute: number | undefined;

    for (const segment of hourField.split(',')) {
      const local = convertSimpleUtcFire(parseInt(segment.trim(), 10), utcMinute);
      localHours.push(local.hour);
      if (localMinute === undefined) {
        localMinute = local.minute;
      }
    }

    const localParts = [...parts];
    localParts[0] = (localMinute ?? utcMinute).toString();
    localParts[1] = [...new Set(localHours)].sort((a, b) => a - b).join(',');
    return localParts.slice(0, 5).join(' ');
  }

  if (isSimpleMinute && isSimpleHour) {
    const local = convertSimpleUtcFire(parseInt(hourField, 10), parseInt(minuteField, 10));
    const localParts = [...parts];
    localParts[0] = local.minute.toString();
    localParts[1] = local.hour.toString();
    return localParts.slice(0, 5).join(' ');
  }

  const localParts = [...parts];
  localParts[1] = convertUtcToLocalHours(localParts[1], viewerTimeZone);
  return localParts.slice(0, 5).join(' ');
}

/**
 * Get days of week from cron expression [0-6] where 0=Sunday
 */
export function getDaysFromCronExpression(cronExpression: string): number[] {
  if (!cronExpression) return [];

  try {
    const parts = parseCronExpression(cronExpression);
    if (!parts) return [];

    const { dayOfWeek } = parts;

    // Handle wildcard - all days
    if (dayOfWeek === '*' || dayOfWeek === '?') {
      return [0, 1, 2, 3, 4, 5, 6];
    }

    // Handle ranges like "1-5"
    const rangeMatch = dayOfWeek.match(/^(\d+)-(\d+)$/);
    if (rangeMatch) {
      const start = parseInt(rangeMatch[1], 10);
      const end = parseInt(rangeMatch[2], 10);
      const days: number[] = [];
      for (let i = start; i <= end; i++) {
        days.push(i % 7);
      }
      return days;
    }

    // Handle comma-separated values like "1,3,5"
    if (dayOfWeek.includes(',')) {
      return dayOfWeek.split(',').map((d) => parseInt(d.trim(), 10) % 7);
    }

    // Handle single value
    const single = parseInt(dayOfWeek, 10);
    if (!isNaN(single)) {
      return [single % 7];
    }

    return [];
  } catch {
    return [];
  }
}

/**
 * Get next run time from cron expression
 */
export function getNextRunTime(cronExpression: string): Date | null {
  if (!cronExpression) return null;

  try {
    const interval = cronParser.parse(cronExpression);
    return interval.next().toDate();
  } catch {
    return null;
  }
}

/**
 * Get previous run time from cron expression
 */
export function getPreviousRunTime(cronExpression: string): Date | null {
  if (!cronExpression) return null;

  try {
    const interval = cronParser.parse(cronExpression);
    return interval.prev().toDate();
  } catch {
    return null;
  }
}

/**
 * Validate a cron expression
 */
export function isValidCronExpression(cronExpression: string): boolean {
  if (!cronExpression) return false;

  try {
    cronParser.parse(cronExpression);
    return true;
  } catch {
    return false;
  }
}

/**
 * Build cron expression for daily schedule
 */
export function buildDailyCronExpression(timeUTC: string, days?: number[]): string {
  if (!timeUTC) return '';

  const [hours, minutes] = timeUTC.split(':').map((p) => parseInt(p, 10));

  const daysPart = days && days.length > 0 && days.length < 7 ? days.join(',') : '*';

  return `${minutes} ${hours} * * ${daysPart}`;
}

/**
 * Build cron expression for repeating schedule
 */
export function buildRepeatingCronExpression(
  frequency: number,
  unit: 'minute' | 'hour',
  days?: number[]
): string {
  const daysPart = days && days.length > 0 && days.length < 7 ? days.join(',') : '*';

  if (unit === 'minute') {
    return `*/${frequency} * * * ${daysPart}`;
  }

  // Hourly
  return `0 */${frequency} * * ${daysPart}`;
}

/**
 * Normalize a cron expression for display, handling edge cases like
 * hour ranges that cross midnight (a UTC conversion artifact).
 * Ported from legacy: dataProtect/src/components/utils/cron.js prettyExpression
 */
export function prettyExpression(currentCronExp: string): string {
  if (!currentCronExp) return '';

  let updatedCron = currentCronExp;
  let parsedHr: string | undefined;
  const parts = currentCronExp.split(' ');
  const hour = parts[1];

  // If the hour already contains commas (e.g. "22-23,0-2" produced by
  // convertUtcToLocalHours when crossing midnight), it's already normalised
  // — skip further range manipulation so we don't corrupt it.
  if (hour && !hour.includes(',')) {
    if (hour.includes('-')) {
      if (hour.includes('/')) {
        const [range, inc] = hour.split('/');
        const [a, b] = range.split('-');
        if (parseInt(a, 10) > parseInt(b, 10)) {
          parsedHr = a === '23' ? `0-${b}/${inc},23` : `0-${b}/${inc},${a}-23/${inc}`;
        } else if (parseInt(a, 10) === parseInt(b, 10)) {
          parsedHr = `${a}/${inc}`;
        }
      } else {
        const [a, b] = hour.split('-');
        if (parseInt(a, 10) > parseInt(b, 10)) {
          parsedHr = `0-${b},${a}-23`;
        } else if (parseInt(a, 10) === parseInt(b, 10)) {
          parsedHr = a;
        }
      }
    } else if (hour.includes('/')) {
      const [range] = hour.split('/');
      const [a] = range.split('-');
      if (a === '23') {
        parsedHr = '23';
      }
    }
  }

  if (parsedHr) {
    const arr = currentCronExp.split(' ');
    arr[1] = parsedHr;
    updatedCron = arr.slice(0, 5).join(' ');
  } else {
    updatedCron = currentCronExp.split(' ').slice(0, 5).join(' ');
  }

  return updatedCron;
}

/**
 * Produce a compact frequency string from a cron expression.
 *
 * 1. Normalize the cron via `prettyExpression` → sanitize
 * 2. Generate a human description via cronstrue
 * 3. Abbreviate: "Every" → 🔁, "hours"/"hour" → H, "minutes"/"minute" → M,
 *    drop "between", replace "and" → "-", and strip the trailing day-of-week
 *    clause (last comma-segment) to keep it short.
 *
 * Ported from legacy: dataProtect/src/components/utils/cron.js formatCronString
 */
export function formatCronStringCompact(cronExpression: string): string {
  if (!cronExpression) return '';

  try {
    const sanitized = sanitizeCronForCronstrue(cronExpression);
    const description = cronstrue.toString(sanitized, {
      throwExceptionOnParseError: false,
      use24HourTimeFormat: false,
    });

    if (description.startsWith(CRONSTRUE_ERROR_PREFIX)) {
      return cronExpression;
    }

    // Legacy compact formatting: drop the last comma-separated segment
    // (usually the day-of-week clause like ", Monday through Friday")
    const segments = description.split(',');
    const trimmed = segments.length > 1 ? segments.slice(0, -1).join(',') : segments[0];

    let compact = trimmed;
    compact = compact.replace(/every/gi, '🔁');
    compact = compact.replace(/between/gi, '');
    compact = compact.replace(/\band\b/gi, '-');
    compact = compact.replace(/hours/gi, 'H');
    compact = compact.replace(/hour/gi, 'H');
    compact = compact.replace(/minutes/gi, 'M');
    compact = compact.replace(/minute/gi, 'M');

    return compact.trim();
  } catch {
    return cronExpression;
  }
}
